import { useEffect, useRef, useState } from 'preact/hooks';
import { MediaVideo } from '../components/MediaEl';
import {
  ApiError,
  listMedia,
  listRecordsByType,
  selectMedia,
  uploadMedia,
  type ActorMedia,
  type MediaKind,
} from '../lib/api';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { mediaLabel, mstr, mbool } from './materialsPresentation';

export interface MaterialsReelsProps {
  tenantId: string;
}

// B5b — REELS: the current reel plays inline as a <video> (object-URL pattern);
// clips group by project; a version history lists the older cuts a reel
// superseded. Uploading adds a reel or a clip (the browser sets the multipart
// boundary); making another reel current is POST select { is_current } — the
// backend demotes the prior cut into the supersedes chain.
export function MaterialsReels({ tenantId }: MaterialsReelsProps) {
  const [reels, setReels] = useState<ActorMedia[] | null>(null);
  const [clips, setClips] = useState<ActorMedia[] | null>(null);
  const [reelsFailure, setReelsFailure] = useState<ReadFailure | null>(null);
  const [clipsFailure, setClipsFailure] = useState<ReadFailure | null>(null);
  // projectId -> title, so a clip group wears its project's name.
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [kind, setKind] = useState<MediaKind>('reel');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function refresh() {
    let cancelled = false;
    void Promise.allSettled([listMedia(tenantId, 'reel'), listMedia(tenantId, 'clip')]).then(([r, c]) => {
      if (cancelled) return;
      if (r.status === 'fulfilled') {
        setReels(r.value);
        setReelsFailure(null);
      } else {
        setReels([]);
        setReelsFailure(describeReadFailure(r.reason));
      }
      if (c.status === 'fulfilled') {
        setClips(c.value);
        setClipsFailure(null);
      } else {
        setClips([]);
        setClipsFailure(describeReadFailure(c.reason));
      }
    });
    // The group names are additive — a failed read costs the names, never
    // the clips (groups fall back to a plain label).
    void listRecordsByType(tenantId, 'actor_project')
      .then((rows) => {
        if (cancelled) return;
        setProjectNames(
          new Map(
            rows.map((r) => [
              r.id,
              typeof r.fields['title'] === 'string' && r.fields['title'] ? (r.fields['title'] as string) : r.display_name,
            ])
          )
        );
      })
      .catch(() => { /* groups fall back to a plain label */ });
    return () => {
      cancelled = true;
    };
  }

  useEffect(refresh, [tenantId]);

  async function makeCurrent(m: ActorMedia) {
    if (busy) return;
    setBusy(m.id);
    setCurrentError(null);
    try {
      await selectMedia(tenantId, m.id, { is_current: true });
      refresh();
    } catch (err) {
      setCurrentError(
        `The current reel didn't change — ${err instanceof ApiError ? err.message : 'the server could not be reached'}`
      );
    } finally {
      setBusy(null);
    }
  }

  async function onFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadMedia(tenantId, kind, file, label.trim() ? { label: label.trim() } : {});
      setLabel('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      input.value = '';
    }
  }

  const reelList = reels ?? [];
  const current = reelList.find((r) => mbool(r.fields, 'is_current')) ?? reelList[0] ?? null;
  const history = reelList.filter((r) => r.id !== current?.id);

  // Clips grouped by project (projectId; unlinked clips fall under "Other").
  const clipList = clips ?? [];
  const groups = new Map<string, ActorMedia[]>();
  for (const c of clipList) {
    const key = c.projectId ?? '_none';
    const arr = groups.get(key) ?? [];
    arr.push(c);
    groups.set(key, arr);
  }

  const loading = reels === null || clips === null;

  return (
    <div class="materials__scroll">
      <div class="materials__grp">Current reel</div>
      {loading ? (
        <div class="materials__empty">One moment…</div>
      ) : current ? (
        <div class="reel">
          <MediaVideo tenantId={tenantId} id={current.id} label={mediaLabel(current)} />
          <div class="reel__meta">
            <span class="reel__t">{mediaLabel(current)}</span>
            {mstr(current.fields, 'duration') ? <span class="reel__d">{mstr(current.fields, 'duration')}</span> : null}
          </div>
          {mstr(current.fields, 'category') ? <div class="reel__cat">{mstr(current.fields, 'category')}</div> : null}
        </div>
      ) : reelsFailure ? (
        <div class="materials__error">
          {reelsFailure.headline} {reelsFailure.detail}
        </div>
      ) : (
        <div class="materials__empty">No reel yet.</div>
      )}

      <div class="materials__grp">Clips by project</div>
      {clipsFailure ? (
        <div class="materials__error">
          {clipsFailure.headline} {clipsFailure.detail}
        </div>
      ) : clipList.length === 0 ? (
        <div class="materials__soft">No clips yet.</div>
      ) : (
        [...groups.entries()].map(([key, arr]) => (
          <div key={key} class="clipgroup">
            <div class="materials__soft">
              {key === '_none' ? 'Other' : projectNames.get(key) ?? 'A project'}
            </div>
            {arr.map((c) => (
              <div key={c.id} class="kvline">
                <span class="kvline__k">{mstr(c.fields, 'category') ?? mediaLabel(c)}</span>
                <span class="kvline__v">{mstr(c.fields, 'duration') ?? ''}</span>
              </div>
            ))}
          </div>
        ))
      )}

      <div class="materials__grp">History</div>
      {history.length === 0 ? (
        <div class="materials__soft">Older cuts will be kept here.</div>
      ) : (
        <details class="reel-history">
          <summary class="reel-history__summary">{history.length} older cut{history.length === 1 ? '' : 's'} kept</summary>
          {history.map((r) => (
            <div key={r.id} class="reel-history__row">
              <span class="reel-history__t">
                {mstr(r.fields, 'version_label') ?? mediaLabel(r)}
                {mstr(r.fields, 'duration') ? ` · ${mstr(r.fields, 'duration')}` : ''}
              </span>
              <button
                type="button"
                class="toggle"
                disabled={busy === r.id}
                onClick={() => makeCurrent(r)}
              >
                Set current
              </button>
            </div>
          ))}
        </details>
      )}
      {currentError ? <div class="materials__error">{currentError}</div> : null}

      <div class="addmedia">
        <div class="addmedia__kinds" role="group" aria-label="What to add">
          <button
            type="button"
            class={`toggle${kind === 'reel' ? ' toggle--on' : ''}`}
            aria-pressed={kind === 'reel'}
            onClick={() => setKind('reel')}
          >
            Reel
          </button>
          <button
            type="button"
            class={`toggle${kind === 'clip' ? ' toggle--on' : ''}`}
            aria-pressed={kind === 'clip'}
            onClick={() => setKind('clip')}
          >
            Clip
          </button>
        </div>
        <input
          class="addmedia__label"
          type="text"
          placeholder="Label"
          value={label}
          onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)}
        />
        <button type="button" class="addmedia__btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? 'Adding…' : `+ Add ${kind}`}
        </button>
        <input
          ref={fileRef}
          class="addmedia__input"
          type="file"
          accept="video/*"
          aria-label="Add reel or clip file"
          onChange={onFile}
        />
      </div>
      {error ? <div class="materials__error">{error}</div> : null}
    </div>
  );
}
